import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import StarFill from '../icons/StarFill';
import { config } from '../../config';
import { fetchStarredBoards } from '../../redux/Slices/boardsSlice';
import { fetchUser } from '../../redux/Slices/thunks';
import Group from '../icons/Group';
import IconPublic from '../icons/Public';
import Lock from '../icons/Lock';
import Home from '../icons/Home';
import Workspaces from '../icons/Workspaces';

const NavbarLeft = ({ boardId, board, setBoard, isReadOnly = false, isAdmin = false }) => {
    const dispatch = useDispatch();

    // Renaming the board is a board-admin-only action (Trello parity) — a normal
    // board member can open the board but not edit its title.
    const titleReadOnly = isReadOnly || !isAdmin;

    const { handlePopoverClick } = useHandlePopoverClick();
    const { user } = useSelector((state) => state.user);
    const isUserLoaded = user && user.user;

    const [isStarred, setIsStarred] = useState(isUserLoaded && user?.user?.starredBoards?.includes(board?._id) || false);
    const [editedTitle, setEditedTitle] = useState(board?.title || '');
    const [inputWidth, setInputWidth] = useState(0);
    const [isEditingTitle, setIsEditingTitle] = useState(false);

    const hiddenSpanRef = useRef(null);
    const inputRef = useRef(null);

    // Measure synchronously to avoid 1-frame crop during typing
    useLayoutEffect(() => {
        const span = hiddenSpanRef.current;
        const input = inputRef.current;
        if (!span || !input) return;

        // Ensure the measuring element uses the same typography as the input
        const cs = window.getComputedStyle(input);
        span.style.fontFamily = cs.fontFamily;
        span.style.fontSize = cs.fontSize;
        span.style.fontWeight = cs.fontWeight;
        span.style.letterSpacing = cs.letterSpacing;

        // Measure text width precisely
        const textWidth = Math.ceil(span.getBoundingClientRect().width);
        const paddingLeft = parseFloat(cs.paddingLeft) || 0;
        const paddingRight = parseFloat(cs.paddingRight) || 0;
        const borderLeft = parseFloat(cs.borderLeftWidth) || 0;
        const borderRight = parseFloat(cs.borderRightWidth) || 0;

        const nextWidth = Math.max(60, textWidth + paddingLeft + paddingRight + borderLeft + borderRight + 1) - 15;
        setInputWidth(nextWidth);
    }, [editedTitle]);

    useEffect(() => {
        setEditedTitle(board?.title || '');
    }, [board]);

    // Update isStarred when user data changes
    useEffect(() => {
        if (isUserLoaded && board?._id) {
            setIsStarred(user?.user?.starredBoards?.includes(board._id) || false);
        }
    }, [user, board, isUserLoaded]);


    // Toggle star status
    const toggleStar = async () => {
        try {

            const action = isStarred ? 'remove' : 'add';

            const response = await fetch(`${config.API_URI}/v1/users/me/stared`, {
                method: 'PUT',
                headers: {

                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ boardId, action }),
            });

            if (!response.ok) throw new Error('Failed to update star status');

            setIsStarred(!isStarred); // Toggle star state
            dispatch(fetchUser());
            dispatch(fetchStarredBoards());
        } catch (error) {
            console.error('Error toggling star status:', error);
        }
    };

    // Handle saving the edited title
    const handleSaveTitle = async () => {
        try {
            if (editedTitle !== board?.title) {


                const response = await fetch(`${config.API_URI}/v1/boards/${boardId}`, {
                    method: 'PUT',
                    headers: {

                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ title: editedTitle }),
                });

                if (!response.ok) throw new Error('Failed to update board title');

                const updatedBoard = await response.json();
                // setBoard(updatedBoard); // Update parent state
            }
        } catch (error) {
            console.error('Error updating board title:', error);
        } finally {
            setIsEditingTitle(false);
        }
    };

    const handleTitleChange = (e) => {
        setEditedTitle(e.target.value);
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    return (
        <ul className="zoobbe-navbar-left">
            {/* Breadcrumb Navigation */}
            <li className="zoobbe-breadcrumb">
                <nav className="breadcrumb-nav">
                    {board?.workspace?.name && (
                        <>
                            <Link
                                to={`/w/${board.wShortId}/boards`}
                                className="breadcrumb-item"
                            >
                                {/* <Workspaces size={18} /> */}
                                <span>{board.workspace.name}</span>
                            </Link>
                            <span className="breadcrumb-separator">/</span>
                        </>
                    )}
                    <div className="breadcrumb-title-container">
                        {/* Hidden span to measure the text width */}
                        <span
                            ref={hiddenSpanRef}
                            className="zoobbe-board-title-hidden"
                            style={{
                                position: 'absolute',
                                visibility: 'hidden',
                                whiteSpace: 'pre',
                            }}
                        >
                            {editedTitle || 'Untitled'}
                        </span>

                        <input
                            ref={inputRef}
                            className="breadcrumb-title-input"
                            value={editedTitle}
                            onChange={handleTitleChange}
                            onBlur={handleSaveTitle}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { handleSaveTitle(); setIsEditingTitle(false); }
                                if (e.key === 'Escape') setIsEditingTitle(false);
                            }}
                            onFocus={handleFocus}
                            style={{
                                width: `${inputWidth}px`,
                                cursor: titleReadOnly ? 'default' : 'text',
                            }}
                            data-tooltip-content={titleReadOnly ? (isReadOnly ? 'Board Title (view only)' : 'Only board admins can rename the board') : 'Board Title'}
                            data-tooltip-position="bottom"
                            readOnly={titleReadOnly}
                        />
                    </div>
                </nav>
            </li>



            <li
                className="zoobbe-board-visibility"
                onClick={(e) => {
                    if (!isReadOnly) {
                        handlePopoverClick(e, 'boardVisibility', {
                            boardId,
                            visibility: board.visibility,
                            setBoard,
                            board,
                            workspaceName: board?.workspace?.name,
                        })
                    }
                }}
                data-popover-trigger={!isReadOnly}
                style={{ cursor: isReadOnly ? 'default' : 'pointer', opacity: isReadOnly ? 0.7 : 1 }}
                data-tooltip-content={isReadOnly ? 'View only' : undefined}
                data-tooltip-position="bottom"
            >
                {board?.visibility === 'Workspace' && (
                    <Group size={18} color='var(--white)' tooltip={{ content: isReadOnly ? 'Workspace visibility (view only)' : 'Workspace visibility', position: 'bottom' }} />
                )}
                {board?.visibility === 'Public' && (
                    <IconPublic size={18} color='var(--white)' tooltip={{ content: isReadOnly ? 'Public (view only)' : 'Public', position: 'bottom' }} />
                )}
                {board?.visibility === 'Private' && (
                    <Lock size={18} color='var(--white)' tooltip={{ content: isReadOnly ? 'Private (view only)' : 'Private', position: 'bottom' }} />
                )}
                {!board?.visibility && (
                    <Lock size={18} color='var(--white)' tooltip={{ content: isReadOnly ? 'Private (view only)' : 'Private', position: 'bottom' }} />

                )}

            </li>
            {!isReadOnly && (
                <li
                    className="zoobbe-star"
                    onClick={toggleStar}
                    data-tooltip-content={isStarred ? 'Starred' : 'Star'}
                    data-tooltip-position="bottom"
                >
                    {!isStarred ? (
                        <span className="material-symbols-outlined">kid_star</span>
                    ) : (
                        <StarFill color={isStarred ? '#FFD700' : '#fff'} />
                    )}
                </li>
            )}
        </ul>
    );
};

export default NavbarLeft;
